import { fetchWithAuth } from '../utils/fetchWithAuth';
import { unwrapResponse as unwrap } from '../utils/unwrapResponse';
import type { InterventionRequest, RefusalReasonCode } from './interventionRequestsApi';

/**
 * The technician's own side of the assignment cycle: the requests he received, and the two answers
 * he gives. `me` everywhere — the subject is the session, never an id a client picks.
 *
 * The back office's side lives in `interventionRequestsApi`, and its types are reused here rather
 * than redeclared: one wire shape, one definition.
 */

/**
 * Mirrors `MyInterventionRequestView` in `backend/types/interventionRequests.ts`.
 *
 * Every field below is nullable, and that is load-bearing: `issue_key` carries no foreign key to
 * `issues` (a sync job TRUNCATEs it), so a request whose mission has vanished must stay readable
 * with `—` in place of its store.
 */
export interface MyInterventionRequest extends InterventionRequest {
  /** The desk's own name — a technician holding the `*` joker receives from several (D27). */
  serviceDeskName: string | null;
  store: string | null;
  client: string | null;
  requestType: string | null;
  description: string | null;
  storeCity: string | null;
  storeAddress: string | null;
  storePhone: string | null;
  /** The desk's own Jira date field, day only. */
  interventionDate: string | null;
  /** 'HH:mm', null when the desk has no time field or the ticket none. */
  interventionTime: string | null;
}

/** The code is required (D25); the free precision is not, and `other` needs no text. */
export interface DeclineRequestInput {
  refusalReasonCode: RefusalReasonCode;
  refusalReason?: string | null;
}

const BASE = 'technicians/me/requests';
const jsonHeaders = { 'Content-Type': 'application/json' };

/**
 * Newest first, and `pending | accepted | declined` only — the server excludes `cancelled` and
 * `superseded`. `?? []` because an envelope without `data` must render an empty list, not crash.
 */
export const fetchMyInterventionRequests = async (): Promise<MyInterventionRequest[]> =>
  (await unwrap<MyInterventionRequest[]>(
    await fetchWithAuth(BASE),
    'Failed to load your intervention requests'
  )) ?? [];

export const acceptMyInterventionRequest = async (id: number): Promise<InterventionRequest> =>
  unwrap<InterventionRequest>(
    await fetchWithAuth(`${BASE}/${id}/accept`, { method: 'POST' }),
    'Failed to accept the intervention request'
  );

export const declineMyInterventionRequest = async (
  id: number,
  input: DeclineRequestInput
): Promise<InterventionRequest> =>
  unwrap<InterventionRequest>(
    await fetchWithAuth(`${BASE}/${id}/decline`, {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify(input),
    }),
    'Failed to decline the intervention request'
  );
